import { useRef } from 'react';
import { CoinOrbit, CoinStage } from '@/components/coin-stage';
import { Reveal } from '@/components/reveal';
import { ScrollExpand } from '@/components/scroll-expand';
import { Section } from '@/components/section';
import { copy } from '@/content/copy';
import { STATS } from '@/content/facts';
import { useMediaQuery } from '@/scroll/use-media-query';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';

const { coins } = copy;

/** Two digits like the HUD elsewhere: 06, 15, 77. */
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * One stage. A disc under the eyebrow shows one coin turning through the six currencies,
 * ringed by an orbit of local rails riding outside the disc; as the page scrolls the disc
 * grows past the corners and the title, lede and stat strip arrive over it. The orbit's chips
 * are the section's list of coins and rails, so nothing repeats below.
 */
export function Coins() {
  const ref = useRef<HTMLElement>(null);
  const narrow = useMediaQuery('(max-width: 767px)');

  useRevealOnEnter(
    ref,
    (tl, q) => {
      tl.to(q('[data-eyebrow]'), { opacity: 1, y: 0, duration: 0.7 }, 0.1).to(
        q('[data-cue]'),
        { opacity: 1, y: 0, duration: 0.6 },
        0.6,
      );
    },
    { start: 'top 60%' },
  );

  return (
    <Section ref={ref} id="coins" padded={false}>
      <ScrollExpand
        triggerId="coins"
        className="coin-figure"
        shape="circle"
        startSize={narrow ? 45 : 32}
        backdrop={<div className="frame-backdrop h-full w-full" />}
        media={<CoinStage />}
        ornament={<CoinOrbit />}
        scrim={<div className="stage-scrim h-full w-full" />}
        resting={
          <Reveal
            as="p"
            data-eyebrow
            className="hud eyebrow m-0 self-start pt-[clamp(5.5rem,13vh,7.5rem)] text-brand-sky"
          >
            <span aria-hidden className="h-px w-6 bg-brand-red" />
            {coins.eyebrow}
          </Reveal>
        }
        hint={
          <Reveal data-cue className="hud grid justify-items-center gap-3 text-muted-foreground">
            <span>{coins.stage.cue}</span>
            <span className="scrollcue-line" />
          </Reveal>
        }
      >
        {/* Expanded stage. Plain markup on purpose: the scrub owns its entrance, so it must
            not carry the [data-reveal] / [data-line] attributes the section's reveal hides. */}
        <div className="grid w-full max-w-[820px] justify-items-center gap-5 text-center sm:gap-6">
          <h2 className="m-0 text-balance text-[clamp(2rem,4.6vw,3.6rem)] font-bold leading-[1.02] tracking-[-0.04em]">
            {coins.title.join(' ')}
          </h2>
          <p className="m-0 max-w-[600px] text-[clamp(1rem,1.5vw,1.2rem)] leading-relaxed text-foreground/80">
            {coins.lede}
          </p>
          <dl className="m-0 mt-2 grid grid-cols-3 gap-6 border-t border-foreground/15 pt-6 sm:gap-10">
            {coins.stats.map((stat) => (
              <div key={stat.label} className="grid gap-1">
                {/* Term first for the document order, value first visually. */}
                <dt className="hud order-2 text-muted-foreground">{stat.label}</dt>
                <dd className="m-0 font-mono text-[clamp(1.5rem,2.4vw,2rem)] font-semibold tabular-nums text-foreground">
                  {pad(STATS[stat.id])}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </ScrollExpand>
    </Section>
  );
}
